// A20 W2b — the licence console's binding of the ported reporter.
//
// NOT a port. Everything above this file (types / strings / useRecorder /
// ReportProvider / ReportButton / ReportDialog) is a copy of @vendorwatch/ui;
// this is the app-specific half that the shared half deliberately knows nothing
// about: the surface key, the endpoint, and a transport carrying the caller's
// own Supabase session.
//
// WHY A CUSTOM TRANSPORT RATHER THAN THE PROVIDER'S DEFAULT FETCH. The default
// posts with `credentials: 'omit'` and no Authorization header — correct for the
// hosted TPRM7 bridge, useless here. The licence console talks to its OWN
// backend on its own session, so this reuses `apiRequest`, which is the single
// place in this app that attaches the bearer token and normalises failures into
// ApiError. POST /api/v1/reports/mine (backend/src/routes/bug-report.routes.ts)
// takes exactly that: supabaseAuthMiddleware, no API key, no bridge secret.
//
// WHAT WE DELIBERATELY DO NOT SEND. `organizationId`, `userId` and `role` are
// left out of the identity. The route derives organization_id from the verified
// session; anything the browser asserted about who it is would be unverifiable,
// and A20's whole posture is that identity comes from the token, never the
// payload. Consequence to be honest about: `reporter_user_id` on the ticket is
// NULL for console-filed reports until the route stamps it from req.user — the
// triage view says "not recorded" rather than inventing a name.

import * as React from 'react';
import { apiRequest, supabase, ApiError } from '@/lib/supabase';
import { ReportProvider } from './ReportProvider';
import { ReportDialog } from './ReportDialog';
import type { ReportEnvelope, ReporterConfig, SubmitResult } from './types';

/** 202 body from POST /reports/mine — `{ accepted, id, ticket_ref, status, … }`. */
interface AcceptedReport {
  accepted?: boolean;
  ticket_ref?: string;
}

/**
 * Relay one envelope to the licence backend.
 *
 * Returns a structured failure rather than throwing: the dialog renders the
 * reason with the user's text still on screen, so a failed submit never costs
 * them what they wrote. There is no branch here that reports success without a
 * 2xx — the customer-portal Support.tsx "we'll respond within 4 hours" lie is
 * exactly what this shape exists to avoid.
 */
async function transport(envelope: ReportEnvelope): Promise<SubmitResult> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      return { ok: false, error: 'Your session has expired — please sign in again.' };
    }
    const res = await apiRequest<AcceptedReport>('/reports/mine', {
      method: 'POST',
      body: JSON.stringify(envelope),
    });
    return { ok: true, ticketRef: res?.ticket_ref };
  } catch (err) {
    if (err instanceof ApiError) {
      // status 0 is apiRequest's offline signal (fetch rejected before a
      // response existed) — say so, rather than showing a bare DNS message.
      if (err.status === 0) {
        return { ok: false, error: 'Could not reach the licence server. Check your connection and try again.' };
      }
      return { ok: false, error: err.message };
    }
    return { ok: false, error: err instanceof Error ? err.message : 'network error' };
  }
}

/**
 * Mounts the reporter context + dialog.
 *
 * A20 C3 — rendered by App.tsx's ReporterHost, ABOVE the authenticated chrome
 * and the routed content, not by AdminHeader. It moved because context flows
 * only downwards: mounted in the header it was a SIBLING of the pages, so an
 * error boundary around a page could never reach it, and a crash could not
 * offer a report carrying the buffer. AdminHeader is now just a consumer
 * (<ReportButton/>), and StudioReporter still SWAPS with this rather than
 * nesting — one recorder, ever.
 */
export function ConsoleReporter({ children }: { children?: React.ReactNode }) {
  const config = React.useMemo<ReporterConfig>(
    () => ({
      // Unused by the custom transport, but the config type requires an
      // endpoint and a wrong-looking one would mislead the next reader.
      endpoint: '/api/v1/reports/mine',
      surfaceKey: 'license.console',
      // Undefined until the console defines a build version (VITE_APP_VERSION
      // is set by no env file today) — the ticket stays actionable on
      // route + surfaceKey, which is the original's stated fallback.
      appVersion: import.meta.env.VITE_APP_VERSION as string | undefined,
      transport,
      // Identity the browser genuinely owns: nothing. See the header note.
      getIdentity: () => ({}),
      // Lets an operator kill capture fleet-wide without redeploying the widget.
      captureDisabled: import.meta.env.VITE_REPORTER_CAPTURE === 'off',
    }),
    [],
  );

  return (
    <ReportProvider config={config}>
      {children}
      <ReportDialog />
    </ReportProvider>
  );
}

export default ConsoleReporter;
